import { useMemo } from "react";
import { motion } from "framer-motion";
import { 
  Shirt, Umbrella, Sun, Snowflake, Wind, 
  Glasses, CloudRain, Thermometer, Droplets
} from "lucide-react";

const CLOTHING_RULES = {
  freezing: {
    temp: [-999, 32],
    icon: Snowflake,
    color: "text-blue-400",
    bg: "bg-blue-500/20",
    title: "Bundle Up!",
    items: ["Heavy winter coat", "Warm hat & gloves", "Scarf", "Insulated boots", "Layers underneath"]
  },
  cold: {
    temp: [32, 50],
    icon: Thermometer,
    color: "text-cyan-400",
    bg: "bg-cyan-500/20",
    title: "Dress Warm",
    items: ["Winter jacket", "Sweater or hoodie", "Long pants", "Closed-toe shoes", "Light gloves optional"]
  },
  cool: {
    temp: [50, 65],
    icon: Wind,
    color: "text-teal-400",
    bg: "bg-teal-500/20",
    title: "Layer Up",
    items: ["Light jacket or cardigan", "Long sleeves", "Jeans or pants", "Comfortable sneakers"]
  },
  mild: {
    temp: [65, 75],
    icon: Shirt,
    color: "text-green-400",
    bg: "bg-green-500/20",
    title: "Perfect Weather",
    items: ["T-shirt or light top", "Light pants or jeans", "Sneakers or casual shoes", "Light layers for evening"]
  },
  warm: {
    temp: [75, 85],
    icon: Sun,
    color: "text-yellow-400",
    bg: "bg-yellow-500/20",
    title: "Stay Cool",
    items: ["Light breathable clothing", "Shorts or light pants", "Sandals or breathable shoes", "Sunglasses", "Hat for sun protection"]
  },
  hot: {
    temp: [85, 999],
    icon: Sun,
    color: "text-orange-400",
    bg: "bg-orange-500/20",
    title: "Beat the Heat",
    items: ["Light, loose clothing", "Shorts and tank top", "Sandals", "Sunglasses & sunscreen", "Stay hydrated!"]
  }
};

export default function WhatToWear({ weather, forecast }) {
  const recommendation = useMemo(() => {
    if (!weather) return null;
    
    const temp = weather.main?.temp || 70;
    const feelsLike = weather.main?.feels_like || temp;
    const humidity = weather.main?.humidity || 50;
    const windSpeed = weather.wind?.speed || 0;
    const weatherCondition = weather.weather?.[0]?.main?.toLowerCase() || "";
    const weatherDesc = weather.weather?.[0]?.description || "";
    
    // Find base clothing recommendation
    let baseRec = null;
    for (const [key, rule] of Object.entries(CLOTHING_RULES)) {
      if (feelsLike >= rule.temp[0] && feelsLike < rule.temp[1]) {
        baseRec = { ...rule, key };
        break;
      }
    }
    
    if (!baseRec) baseRec = CLOTHING_RULES.mild;
    
    // Add weather-specific items
    const extraItems = [];
    const warnings = [];
    
    if (weatherCondition.includes("rain") || weatherCondition.includes("drizzle") || weatherCondition.includes("thunderstorm")) {
      extraItems.push({ icon: Umbrella, text: "Bring an umbrella", color: "text-blue-400" });
      extraItems.push({ icon: CloudRain, text: "Waterproof jacket", color: "text-blue-400" });
    }
    
    if (weatherCondition.includes("snow")) {
      extraItems.push({ icon: Snowflake, text: "Waterproof boots", color: "text-cyan-400" });
    }
    
    if (windSpeed > 15) {
      extraItems.push({ icon: Wind, text: "Windbreaker recommended", color: "text-gray-400" });
    }
    
    if (humidity > 80 && feelsLike > 75) {
      warnings.push("High humidity - wear moisture-wicking fabrics");
    }
    
    // Check for UV (if daytime and clear)
    const hour = new Date().getHours();
    if (hour >= 10 && hour <= 16 && (weatherCondition.includes("clear") || weatherCondition.includes("sun"))) {
      extraItems.push({ icon: Glasses, text: "Sunglasses & sunscreen", color: "text-yellow-400" });
    }
    
    return {
      ...baseRec,
      extraItems,
      warnings,
      temp: Math.round(temp),
      feelsLike: Math.round(feelsLike),
      condition: weatherDesc
    };
  }, [weather]);

  if (!recommendation) {
    return (
      <div className="flex items-center justify-center h-32">
        <p className="text-white/40">Loading recommendations...</p>
      </div>
    );
  }

  const Icon = recommendation.icon;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Shirt className="w-5 h-5 text-[#ff0080]" />
        <h3 className="font-semibold text-white/80">What to Wear</h3>
      </div>
      
      {/* Main Recommendation */}
      <div className="flex items-start gap-4 mb-4">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`w-16 h-16 rounded-2xl ${recommendation.bg} flex items-center justify-center`}
        >
          <Icon className={`w-8 h-8 ${recommendation.color}`} />
        </motion.div>
        <div className="flex-1">
          <p className={`font-semibold text-lg ${recommendation.color}`}>{recommendation.title}</p>
          <p className="text-sm text-white/50">
            {recommendation.feelsLike}°F feels like • {recommendation.condition}
          </p>
        </div>
      </div>
      
      {/* Clothing Items */}
      <div className="space-y-2 mb-4">
        {recommendation.items.slice(0, 4).map((item, idx) => (
          <motion.div
            key={`item-${idx}`}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="flex items-center gap-2 text-sm"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
            <span className="text-white/70">{item}</span>
          </motion.div>
        ))}
      </div>
      
      {/* Extra Items (rain, wind, etc.) */}
      {recommendation.extraItems.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/10">
          {recommendation.extraItems.map((item, idx) => {
            const ItemIcon = item.icon;
            return (
              <div 
                key={`item-${idx}`}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/5 text-xs"
              >
                <ItemIcon className={`w-3 h-3 ${item.color}`} />
                <span className="text-white/60">{item.text}</span>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Warnings */}
      {recommendation.warnings.length > 0 && (
        <div className="mt-3 p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
          <p className="text-xs text-yellow-400">{recommendation.warnings[0]}</p>
        </div>
      )}
    </div>
  );
}
